/**
 * A SERVICE as the technician works it, read off its tasks.
 *
 * The manager side retired tasks per service (2026-09-03): a job is agreed,
 * assigned, timed and finished as its services. The backend still mints
 * exactly one task under every service and keeps the timer, the notes and
 * "done" on that task — so the screens name the service and act on its
 * task through `taskId`. Nothing here is shown as a step.
 *
 * One derivation for the Today card and the job page, so the two never
 * disagree about what state a service is in or which button it gets.
 */
import type { DoListServiceState, ServiceStatus } from '@/apis/types'
import { taskMinutes, type TimedMinutes } from '@/lib/taskTime'

/**
 * The service fields the derivation reads — both wire shapes carry them
 * (`mechanicName` on the detail, `technicianName` on the do-list).
 */
export interface ServiceViewSource extends TimedMinutes {
  readonly id: string
  readonly status: ServiceStatus
  readonly state: DoListServiceState
  readonly mine: boolean
  readonly mechanicName?: string | null
  readonly technicianName?: string | null
}

export interface ServiceView {
  /** open | claimed | in_progress | paused | done | awaiting_approval */
  readonly state: DoListServiceState
  /** The signed-in technician holds this service. */
  readonly mine: boolean
  /** Whoever holds it; null for the floor pool. */
  readonly mechanicName: string | null
  /** Closed time on the service, minutes. */
  readonly loggedMinutes: number
  /** Start instant of the running timer, if one runs. */
  readonly runningSince: string | null
}

/**
 * Read a service's state off the service.
 *
 * <p>This used to RECONSTRUCT that state from the service's steps: sum
 * their minutes, find whichever one was running, filter the outstanding
 * ones and run a `pickTask` priority list (in_progress > paused > claimed >
 * open, mine first) to choose the step the buttons would act on — plus a
 * branch for a service with no step at all, which could not be timed.
 *
 * <p>None of that was telling us anything. Every service minted exactly one
 * step, named after itself, and the server has derived the state on the
 * service since the steps were retired. What is left is the mapping this
 * screen actually needed.
 */
export function serviceView(svc: ServiceViewSource): ServiceView {
  return {
    state: svc.state,
    mine: svc.mine,
    mechanicName: svc.mechanicName ?? svc.technicianName ?? null,
    loggedMinutes: svc.loggedMinutes,
    runningSince: svc.runningSince,
  }
}

/** Live minutes on a service right now — closed time plus the running span. */
export function serviceViewMinutes(view: ServiceView, now: number): number {
  return taskMinutes({ loggedMinutes: view.loggedMinutes, runningSince: view.runningSince }, now)
}

/**
 * Agreed, and in nobody's hands: what "Auftrag übernehmen" (and, once the
 * technician already holds a stake on the job, the per-service Take) takes.
 */
export function isLooseServiceSource(svc: ServiceViewSource): boolean {
  return svc.status === 'OPEN'
    && !svc.mine
    && (svc.mechanicName ?? svc.technicianName ?? null) === null
}
